import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentRefinements } from 'react-instantsearch';
import { useMediaQuery } from 'react-responsive';
import { trackEvent } from '@cx-web/feature-tracking/dataLayer';
import { type RepubIs } from '@cx-web/utils';
import { type SearchResults } from 'algoliasearch-helper';
import clsx from 'clsx';
import { type Hit } from 'instantsearch.js';

import { type BookmakersConfig } from '@cx-modules/types';

import { type BetInsightsConfig } from '~/configuration/BetInsightsConfiguration';
import { type BettingInsightsContent, type BettingInsightsResult, type EsIndex } from '~/configuration/types';
import useChangedRefinements from '~/hooks/useChangedRefinements.ts';
import { sendGzScrollMessage } from '~/pages/gz/scripts';

import { trackMobileFiltersClick } from '../../tracking';
import { transformRefinements } from '../../transformRefinements';

import styles from './styles/Sidebar.module.css';
import { Dialog, DialogTrigger } from './Dialog';
import { Filters } from './Filters';
import { FiltersWall } from './FiltersWall';

function AvailableResults<TIndex extends EsIndex, TResult extends BettingInsightsResult<TIndex>>({
  items,
  results,
}: {
  items: Hit<TResult>[];
  results?: SearchResults<Hit<TResult>>;
}) {
  const { t } = useTranslation();

  return (
    <p className={styles.availableResults}>
      {t('CX_PLUS_BET_COUNT', { amountOfBets: `${items.length}`, totalBets: `${results?.nbHits}` })}
    </p>
  );
}

export function Sidebar<
  TIndex extends EsIndex,
  TContent extends BettingInsightsContent<TIndex>,
  TResult extends BettingInsightsResult<TIndex>,
>({
  bookmakers,
  canSort,
  config,
  currentPath,
  indexName,
  isLoggedIn,
  items,
  repubIs,
  repubPath,
  resourcesUrl,
  results,
  variant = 'default',
}: {
  bookmakers: BookmakersConfig;
  canSort: boolean;
  config: BetInsightsConfig<TIndex, TContent>;
  currentPath: string;
  indexName: TIndex;
  isLoggedIn: boolean;
  items: Hit<TResult>[];
  repubIs: RepubIs;
  repubPath: string;
  resourcesUrl: string;
  results?: SearchResults<Hit<TResult>>;
  variant?: 'default' | 'compact';
}) {
  const isMobile = useMediaQuery({ maxWidth: 800 });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const { items: refinements } = useCurrentRefinements();
  const changedRefinements = useChangedRefinements(refinements);

  const openMobileMenu = () => {
    if (repubIs.GZ) sendGzScrollMessage({ isOpen: !isMobileMenuOpen, variant: 'filters' });

    setIsMobileMenuOpen(!isMobileMenuOpen);
    trackMobileFiltersClick();
  };

  return (
    <>
      {isMobile ? (
        <>
          <section className={clsx(styles.infoContainer, styles[variant])}>
            <AvailableResults items={items} results={results} />
            <div className={styles.mobileMenuTrigger}>
              <DialogTrigger
                filtersCount={changedRefinements.length}
                isOpen={isMobileMenuOpen}
                isWalled={config.isWalled}
                onClick={openMobileMenu}
                ref={mobileMenuTriggerRef}
              />
            </div>
          </section>
          <Dialog
            data-testid="mobile-refinements"
            isOpen={isMobileMenuOpen}
            isWalled={config.isWalled}
            onApply={() => {
              trackEvent('apply_filter', {
                eventLabel: 'Apply Filter',
                refinements: transformRefinements(refinements),
              });
            }}
            setIsOpen={openMobileMenu}
            title="Refinements"
            triggerRef={mobileMenuTriggerRef}
          >
            <Filters
              bookmakers={bookmakers}
              canSort={canSort}
              components={config.ui.refinements.components}
              config={config}
              indexName={indexName}
              resourcesUrl={resourcesUrl}
            />
          </Dialog>
        </>
      ) : (
        <aside className={styles.sidebar} data-testid="refinements">
          <AvailableResults items={items} results={results} />
          <FiltersWall
            currentPath={currentPath}
            isDisabled={config.isWalled}
            isLoggedIn={isLoggedIn}
            repubIs={repubIs}
            repubPath={repubPath}
            wallType={config.wallType}
          >
            <Filters
              bookmakers={bookmakers}
              canSort={canSort}
              components={config.ui.refinements.components}
              config={config}
              indexName={indexName}
              resourcesUrl={resourcesUrl}
            />
          </FiltersWall>
        </aside>
      )}
    </>
  );
}
